import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  inject,
  Input,
} from '@angular/core';
import { CourseDialogComponent } from '../../Dialogs/course-dialog/course-dialog.component';
import { Course, CourseCategory } from 'TrainingTrackingDataTypes';
import {
  ListViewComponent,
  AutoDropdownComponent,
  listRow,
  getExtraBurgerMenuOptions,
  MenuOption,
} from '@standard-angular-utilities/shared-components';
import { LogDTO, LogType, User } from 'BaseDataTypes';
import { UserService } from 'BaseServices/user-service';

import { FormsModule } from '@angular/forms';
import { CourseService } from 'TrainingTrackingServices/course-service';
import { CourseEventLogService } from 'TrainingTrackingServices/course-event-log-service';
import { CourseCategoryService } from 'TrainingTrackingServices/course-categories-service';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { SharedFunctionsService } from 'BaseServices/sharedFunctions.service';
import { LogService } from 'BaseServices/log-service';
import { LogHelperService } from 'BaseServices/log-helper.service';

@Component({
  selector: 'app-course-view',
  templateUrl: './course-view.component.html',
  styleUrls: [
    '../../../../../../src/app/general-styles/pages.css',
    './course-view.component.css',
  ],
  imports: [
    ListViewComponent,
    FormsModule,
    MatButtonModule,
    AutoDropdownComponent,
  ],
})
export class CourseViewComponent implements OnInit, AfterViewInit {
  @ViewChild('listView') listView!: ListViewComponent<Course>;
  @ViewChild('logListView') logListView!: ListViewComponent<LogDTO>;
  @Input() courseCategory!: CourseCategory;

  readonly dialog = inject(MatDialog);

  onChangeFunction: () => void = () => {};

  courses: Course[] = [];
  courseHeaderList: string[] = ['Navn', 'Kategori'];
  logHeaderList: string[];
  headerList: string[] = this.courseHeaderList;
  isHistory: boolean = false;
  users: User[] = [];

  currentUser = this.userService.currentUser;

  courseCategories: CourseCategory[] = [];
  selectedCategory: number = 0;

  extraBurgerMenuOptions: MenuOption[] = [];

  constructor(
    private courseService: CourseService,
    private courseEventLogService: CourseEventLogService,
    private courseCategoryService: CourseCategoryService,
    private userService: UserService,
    private sharedFunctionsService: SharedFunctionsService,
    private logService: LogService,
    private logHelper: LogHelperService
  ) {
    this.logHeaderList = this.logService.getLogHeaders('Kursus');
  }

  /**
   * Function which creates a default course, and opens it in the dialog
   */
  createNewCourse() {
    let defaultCourse: Course = {
      id: 0,
      name: '',
      description: '',
      courseLink: '',
      yearsValid: 0,
      courseCategoryId: 0,
    };
    this.openCourseDialog(defaultCourse);
  }

  /**
   * Function which opens CourseDialogComponent with the supplied course
   * @param course
   */
  openCourseDialog(course: Course) {
    let dialogref = this.dialog.open(CourseDialogComponent, {
      data: { data: [{ ...course }] },
    });
    dialogref.afterClosed().subscribe((result) => {
      if (result == true) {
        this.updateListWithCourseData();
      }
    });
  }

  ngOnInit(): void {
    const isAdmin =
      this.userService.getSelectedRole ===
      this.userService.getUserRoleTypes.Admin;
    const customLabel = 'Skift til kursusoversigt';

    this.extraBurgerMenuOptions = getExtraBurgerMenuOptions(
      isAdmin,
      this.isHistory,
      customLabel,
      this.switchDataInList.bind(this),
      () => this.extraBurgerMenuOptions[0][0],
      (newLabel: string) => {
        this.extraBurgerMenuOptions[0][0] = newLabel;
      }
    );

    this.getAllUsers();
  }

  ngAfterViewInit(): void {
    this.updateListWithCourseData();
    this.courseCategoryService.getCourseCategories().subscribe((result) => {
      this.courseCategories = result;
    });

    if (this.listView) {
      this.onChangeFunction = this.listView.update.bind(this.listView);
    }
  }

  /**
   * Gets users from backend, users are used by the course event log.
   */
  getAllUsers() {
    this.userService.getUsers().subscribe((result) => {
      this.users = result;
    });
  }

  /**
   * Filter function given to list-view.
   * @param course
   * @returns
   */
  filterFunction = (course: Course) => {
    if (this.isHistory) {
      //EventLog mode - got no filters right now
      return true;
    } else {
      //Course mode
      if (
        this.selectedCategory != 0 &&
        course.courseCategoryId != this.selectedCategory
      ) {
        return false;
      }
      return true;
    }
  };

  /**
   * Helper function for updateListWithLogData
   * @param id
   * @returns Name by course id or "Kursus ikke fundet"
   */
  getCourseNameFromCourseId(id: number): string {
    return (
      this.courses.find((course) => course.id === id)?.name || id.toString()
    );
  }

  /**
   * Function which switches the list between courses and the course event log.
   */
  switchDataInList() {
    if (this.isHistory) {
      this.updateListWithCourseData();
      this.isHistory = false;
    } else {
      this.updateListWithLogData();
      this.isHistory = true;
    }
  }

  /**
   * Function to update list with eventlog data
   */
  updateListWithLogData() {
    this.logHelper.updateLogList(
      LogType.Course,
      this.getCourseNameFromCourseId.bind(this),
      (data) => (this.logListView.listData().listData = data),
      () => this.logListView.update()
    );
  }

  /**
   * Function to update list with course data
   */
  updateListWithCourseData() {
    this.courseService.getCourses().subscribe((result) => {
      let tempList: listRow<Course>[] = [];
      result.forEach((course) => {
        tempList.push({
          object: course,
          id: course.id,
          data: [course.name, course.courseCategory!.name],
        });
      });
      this.courses = result;
      this.headerList = this.courseHeaderList;
      this.listView.listData().listData = tempList;
      this.listView.update();
    });
  }
}
